import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { findIndex as _findIndex, startCase as _startCase } from 'lodash-es';

import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import {
  ResourceSectionType,
  ResourceSuggestionWithDetails,
  SuggestedResourcesSectionWithDetails,
} from '@app/learner-home/learner-home-api.model';
import { ModifyOptionsFnType } from '@app/shared/components/menu/menu.component';

import { SuggestedResourcesService } from '@app/learner-home/services/suggested-resources.service';
import { AuthService } from '@app/shared/services/auth.service';
import { SearchUrlService } from '@app/shared/services/search-url.service';
import { TranslateService } from '@ngx-translate/core';
import {
  LearnerHomeTrackerService,
  SectionTrackingProperties,
} from '@app/learner-home/services/learner-home-tracker.service';
import { ContentCardService } from '@app/shared/components/content/content-card/content-card.service';
import { UserRecommendationHeaderItem } from '@app/shared/components/user-recommendation-header/user-recommendation-header.component';
import { RecommendationInfoPipe } from '@app/learner-home/pipes/recommendation-info.pipe';
import { WindowToken } from '@app/shared/window.token';

interface ViewMore {
  href: string;
  icon: string;
  link: string;
  title: string;
}

@Component({
  selector: 'dgx-suggested-section',
  templateUrl: './suggested-section.component.html',
  styleUrls: ['./suggested-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuggestedSectionComponent implements OnChanges, OnDestroy {
  /**
   *  Controls if the sections is expanded or not
   */
  @Input() public isExpanded: boolean;
  /**
   * Controls if the skeletons should be shown instead of the content.
   */
  @Input() public isLoading: boolean;
  /**
   * Controls if the expand button is showing a loading indicator
   */
  @Input() public isLoadingMore: boolean;
  /**
   * The type of the section we're expecting to see once it's loaded. This is used to control
   * the loading layout for sections that might have a different one.
   */
  @Input() public expectedResourceSection?: ResourceSectionType;
  /**
   * Action to perform when section wants to load more data. This needs to be a bound method
   * instead of an Output because we need to return a promise to this component.
   */
  @Input() public loadMore: (index: number) => Promise<unknown>;
  /**
   * Section that is being displayed. Can be undefined while it's being loaded.
   */
  @Input() public section?: SuggestedResourcesSectionWithDetails;
  /**
   * The index of this section in the list. This is mainly needed for analytics.
   */
  @Input() public sectionIndex: number;
  /**
   * Emits an event when user clicks the "Expand" or "Collapse" buttons
   */
  @Output() public toggleExpanded = new EventEmitter<HTMLElement>();

  @ViewChildren('suggestionElements') public suggestionElements: QueryList<any>;
  /**
   * Determine how many tiles should we show per row.
   */
  @Input() public perRow?: number;

  public visibleSuggestions?: ResourceSuggestionWithDetails[];
  public showExpand = true;
  public showStatistics?: boolean;
  public expandTitle = '';
  // Determines if this section is going to call the passed in loadMore() method
  public loadsMore = false;
  public viewMore?: ViewMore;
  public i18n = this.translateService.instant([
    'Core_Collapse',
    'Core_LoadMore',
    'Core_ShowAll',
  ]);

  // The main title of the section
  public title: string;
  // A smaller title shown above the main title
  public suptitle: string;

  private expandCollapseToggled: boolean = false;

  public constructor(
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private suggestedResourcesService: SuggestedResourcesService,
    private translateService: TranslateService,
    private searchUrlService: SearchUrlService,
    private learnerHomeTrackerService: LearnerHomeTrackerService,
    private recommendationInfoPipe: RecommendationInfoPipe,
    public contentCardService: ContentCardService,
    @Inject(WindowToken) private window: Window
  ) {}

  public get sectionTrackingProperties(): SectionTrackingProperties {
    return {
      sectionIndex: this.sectionIndex,
      sectionItemCount: this.section?.resourceSuggestions?.length,
      sectionType: this.section?.resourceSection,
    };
  }

  public ngOnChanges({
    isExpanded,
    section,
    expectedResourceSection,
  }: SimpleChanges) {
    this.perRow = this.perRow || 3;
    if (expectedResourceSection && !this.section) {
      this.section = {
        resourceSection: this.expectedResourceSection,
        resourceSuggestions: [],
      };
    }
    // If isExpanded or section inputs changed, update visibleSuggestions
    if (isExpanded || section) {
      // Derive the section specific details first, like perRow and viewMore
      if (this.section) {
        this.deriveSectionDetails();
        this.section?.resourceSuggestions.forEach((suggestion) => {
          if (!suggestion.modifyOptionsFn) {
            suggestion.modifyOptionsFn = this.createMenuConfig(suggestion);
          }
        });
      }

      this.updateVisibleSuggestions();
    }
  }

  public ngOnDestroy(): void {
    this.contentCardService.setIndexesOfExpandedCards([]);
  }

  public createMenuConfig(
    suggestion: ResourceSuggestionWithDetails
  ): ModifyOptionsFnType<LearningResourceViewModel> {
    return this.suggestedResourcesService.getModifyOptionsFn({
      onRemove: (suggestion: ResourceSuggestionWithDetails) =>
        this.remove(suggestion),
      suggestion,
      sectionTrackingProperties: this.sectionTrackingProperties,
    });
  }

  public ngAfterViewInit() {
    this.updateRecommendationHeaderHeight();
    this.suggestionElements.changes.subscribe((args) => {
      this.updateRecommendationHeaderHeight();

      // don't focus on the initial load, only if the expand / collapse button has been toggled
      if (this.expandCollapseToggled) {
        const index = this.isExpanded ? this.perRow : 0;
        this.suggestionElements
          .toArray()
          [index].nativeElement.children[1].focus();
      }
    });
  }

  @HostListener('window:resize')
  public onResize() {
    this.updateRecommendationHeaderHeight();
  }

  public getRecommendationHeader(suggestion: ResourceSuggestionWithDetails) {
    return this.recommendationInfoPipe.transform(
      suggestion
    ) as UserRecommendationHeaderItem;
  }

  /**
   * Receives the `dismiss` emission from a menu.component deeper
   * down in the source.
   * TODO: Update this to use a modify function instead.
   *
   * @param element - The source element. May be null if operating server-side.
   * @param suggestion - The current item.
   * @param index - The index of the item.
   */
  public dismiss(
    element: HTMLElement,
    suggestion: ResourceSuggestionWithDetails,
    index: number
  ): void {
    this.suggestedResourcesService
      .dismissItem(
        { isModal: false },
        suggestion,
        this.sectionTrackingProperties,
        element
      )
      .subscribe(() => this.remove(suggestion, index));
  }

  public onToggleExpanded(evt: Event): void {
    const element = (evt.target as HTMLElement).parentElement; // the element is short lived here
    const emit = () => {
      this.toggleExpanded.emit(element);
    };
    if (this.loadsMore) {
      this.loadMore(this.sectionIndex)
        .then(emit)
        .finally(() => {
          // Any further toggles should go straight to the event emitter
          this.onToggleExpanded = emit;
        });
    } else {
      emit();
    }
    this.expandCollapseToggled = true;
  }

  /**
   * Removes the suggestion from the view. Called after the local `dismiss`.
   * TODO: Update this to use a modify function instead.
   *
   * @param suggestion - The current item.
   * @param index - The index of the item.
   */
  public remove(
    suggestion: ResourceSuggestionWithDetails,
    index?: number
  ): void {
    // TODO: Use the new menu.component instead of the old actionOptions.
    // In this case, index will be passed in by *all* uses of this `remove`
    // method, and the check for i to be defined can be removed.
    index =
      index ??
      _findIndex(this.section.resourceSuggestions, [
        'userSuggestionId',
        suggestion.userSuggestionId,
      ]);
    this.section.resourceSuggestions.splice(index, 1);
    this.updateVisibleSuggestions();
    this.cdr.detectChanges();
    this.updateRecommendationHeaderHeight();
  }

  public stubArray() {
    return Array(this.perRow);
  }

  public viewMoreClicked(evt: MouseEvent) {
    evt.stopPropagation();
    evt.preventDefault();
    this.learnerHomeTrackerService.viewMoreClicked(evt.target as HTMLElement);
    this.window.open(this.viewMore.href, '_self');
  }

  public onIntersection(
    item: ResourceSuggestionWithDetails,
    itemIndex: number
  ): void {
    const properties = {
      location: 'Learner Home', // Maps to ActionLocation in Amplitude
      inputType: item.reference.inputType, // Maps to ContentType in Amplitude
      contentId: item.reference.inputId,
      title: item.reference.title,
      providerId: item.reference.providerId,
      providerName: item.reference.providerName,
      externalId: item.reference.externalId,
      position: itemIndex,
      dateCreated: item.reference.dateCreated,
    };
    this.learnerHomeTrackerService.trackContentImpressed(properties);
  }

  private createGroupViewMore(): ViewMore {
    return {
      icon: 'person-double',
      title: this.translateService.instant('LearnerHome_GroupViewMoreTitle'),
      link: this.translateService.instant('LearnerHome_GroupViewMoreLink'),
      href: '/groups/' + encodeURIComponent(this.section.context.id),
    };
  }

  private createOpportunityBrowseViewMore(): ViewMore {
    return {
      icon: 'opportunity',
      title: this.translateService.instant('LearnerHome_ViewMoreTitle'), // Didn't find what you were looking for?
      link: this.translateService.instant(
        'LearnerHome_OpportunitiesViewMoreLink'
      ),
      href: '/career/opportunities',
    };
  }

  private createSearchViewMore(term: string): ViewMore {
    return {
      icon: 'magnifying-glass',
      title: this.translateService.instant('LearnerHome_ViewMoreTitle'),
      link: this.translateService.instant('LearnerHome_ViewMoreLink', {
        term,
      }),
      href: this.searchUrlService.getGlobalSearchURL(term),
    };
  }

  private createUserBrowseViewMore(): ViewMore {
    return {
      icon: 'magnifying-glass',
      title: this.translateService.instant('LearnerHome_ViewMoreUserTitle'),
      link: this.translateService.instant('LearnerHome_ViewMoreUserLink'),
      href: '/learning/people',
    };
  }

  private createMentorBrowseViewMore(): ViewMore {
    return {
      icon: 'magnifying-glass',
      title: this.translateService.instant('LearnerHome_ViewMoreUserTitle'),
      link: this.translateService.instant('LearnerHome_ViewMoreMentorsLink'),
      href: this.searchUrlService.getMentorSearchURL(),
    };
  }

  // Get title, suptitle and the view more card from the current section type
  private deriveSectionDetails() {
    this.title = undefined;
    this.suptitle = undefined;
    this.viewMore = undefined;
    this.showStatistics = false;
    // Default for all rows is 3
    this.perRow = this.perRow || 3;
    // Determine the expand button title based on the isExpanded state and the type of section
    this.expandTitle = this.isExpanded
      ? this.i18n.Core_Collapse
      : this.i18n.Core_LoadMore;

    switch (this.section.resourceSection) {
      case 'ContinueLearning':
        this.expandTitle = this.isExpanded
          ? this.i18n.Core_Collapse
          : this.i18n.Core_ShowAll;
        this.loadsMore = true;
        this.title = this.translateService.instant(
          'LearnerHome_ContinueLearning'
        );
        break;
      case 'FocusSkill':
        this.title = this.section.context?.title ?? '';
        this.suptitle = this.translateService.instant('Core_FocusSkill');
        this.viewMore = this.createSearchViewMore(this.title);
        break;
      case 'GroupActivity':
        this.title = this.section.context?.title ?? '';
        this.suptitle = this.translateService.instant(
          'LearnerHome_RecentActivity'
        );
        this.viewMore = this.createGroupViewMore();
        break;
      case 'Opportunities':
        this.perRow = 2;
        this.title = this.translateService.instant('Core_Opportunities');
        this.viewMore = this.createOpportunityBrowseViewMore();
        break;
      case 'PeopleFollowedActivity':
        this.title = this.translateService.instant(
          'LearnerHome_PeopleYouFollow'
        );
        this.suptitle = this.translateService.instant(
          'LearnerHome_RecentActivity'
        );
        this.showStatistics = true;
        break;
      case 'PeopleToFollow':
        this.title = this.translateService.instant(
          'LearnerHome_BasedOnYourSkills'
        );
        this.suptitle = this.translateService.instant(
          'LearnerHome_PeopleToFollow'
        );
        this.viewMore = this.createUserBrowseViewMore();
        break;
      case 'RecentlyViewed':
        this.expandTitle = this.isExpanded
          ? this.i18n.Core_Collapse
          : this.i18n.Core_ShowAll;
        this.loadsMore = true;
        this.title = this.translateService.instant(
          'LearnerHome_RecentlyViewed'
        );
        break;
      case 'Skill':
        this.title = this.section.context?.title ?? '';
        this.suptitle = this.translateService.instant(
          'dgProfileOverview_AdditionalSkills'
        );
        this.viewMore = this.createSearchViewMore(this.title);
        break;
      case 'SkillExplore':
        this.suptitle = this.translateService.instant(
          'LearnerHome_SkillExplore'
        );
        this.title = this.section.context?.title ?? '';
        this.viewMore = this.createSearchViewMore(this.title);
        break;
      case 'TrendingInOrg':
        this.suptitle = this.translateService.instant(
          'LearnerHome_TrendingWithin'
        );
        this.title =
          this.authService.authUser?.defaultOrgInfo?.name ??
          this.translateService.instant('NativeInviteBanner_AppTitle');
        this.showStatistics = true;
        break;
      case 'ConnectWithAMentor':
        this.title = this.translateService.instant(
          'Opportunities_Marketplace_BasedOn_Focus'
        );
        this.suptitle = this.translateService.instant(
          'LearnerHome_MentorConnect'
        );
        this.viewMore = this.createMentorBrowseViewMore();
        break;
      case 'NewContent':
        this.title = this.translateService.instant('LearningBrowse_NewContent');
        break;
      default:
        // Fall back to displaying the resourceSection itself as the title which
        // is not going to be translated.
        this.title = fallbackTitle(this.section.resourceSection);
    }
  }

  private updateRecommendationHeaderHeight() {
    if (this.section?.resourceSection !== 'ContinueLearning') {
      return;
    }
    const dictionary: { [bottomPosition: number]: HTMLElement[] } = {};
    const list = this.suggestionElements.toArray();

    if (!list.length) {
      return;
    }
    // Group tiles together that are on the same row.
    list.forEach((suggestionElement) => {
      const bottomPosition =
        suggestionElement.nativeElement.getBoundingClientRect().bottom;
      if (!dictionary[bottomPosition]) {
        dictionary[bottomPosition] = [];
      }
      dictionary[bottomPosition].push(suggestionElement.nativeElement);
    });

    for (const [key, arrOfSuggestions] of Object.entries(dictionary)) {
      // check if any suggestions in the row have a recommendation header
      const rowHasRecommendation = arrOfSuggestions.some((val) => {
        return !!this.section.resourceSuggestions.find(
          (suggestion) =>
            `${suggestion.userSuggestionId}` === val.dataset.suggestionId
        )?.recommendation;
      });
      arrOfSuggestions.forEach((val) => {
        const suggestion = this.section.resourceSuggestions.find(
          (suggestion) =>
            `${suggestion.userSuggestionId}` === val.dataset.suggestionId
        );
        suggestion.rowHasHeader = rowHasRecommendation;
      });
    }

    this.cdr.detectChanges();
  }

  private updateVisibleSuggestions() {
    const suggestions = this.section?.resourceSuggestions;
    this.visibleSuggestions = this.isExpanded
      ? suggestions?.slice()
      : suggestions?.slice(0, this.perRow);

    this.showExpand =
      (this.visibleSuggestions &&
        this.visibleSuggestions.length <
          this.section.resourceSuggestions.length) ||
      this.isExpanded;

    // Make room for the viewMore card if we have a full last row of items
    if (
      this.isExpanded &&
      this.viewMore &&
      this.visibleSuggestions?.length % this.perRow === 0
    ) {
      this.visibleSuggestions.splice(this.visibleSuggestions.length - 1, 1);
    }
  }
}

// The assignment to a never type first ensures it's a type error if a new section
// variant is added and not handled above.
const fallbackTitle: (title: never) => string = _startCase;
